import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Upload, FileCheck, Save, Plus, Trash2, User2, Bell } from "lucide-react";
import AlertSettings from "../components/AlertSettings";

export default function Profile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [skillInput, setSkillInput] = useState("");
  const [langInput, setLangInput] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/profile/me").then((r) => { setProfile(r.data.profile); setLoading(false); });
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      const endpoint = user.role === "candidate" ? "/profile/candidate" : "/profile/employer";
      await api.put(endpoint, profile);
      toast.success("Profil enregistré");
    } catch (e) { toast.error("Erreur"); } finally { setSaving(false); }
  };

  const handleCVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload/cv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile({ ...profile, cv_path: r.data.path, cv_filename: r.data.filename });
      toast.success("CV téléchargé");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur upload"); } finally { setUploading(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile({ ...profile, logo_path: r.data.path });
      toast.success("Logo téléchargé");
    } catch { toast.error("Erreur upload"); } finally { setUploading(false); }
  };

  if (loading || !profile) return <div className="max-w-3xl mx-auto p-10 text-slate-500">Chargement...</div>;

  if (user.role === "employer") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-10" data-testid="profile-employer">
        <div className="mec-overline mb-2">Profil entreprise</div>
        <h1 className="font-display text-3xl font-bold text-slate-900 mb-6">Page entreprise</h1>
        <div className="mec-card p-6 space-y-4">
          <div>
            <Label>Logo</Label>
            <div className="mt-1 flex items-center gap-3">
              {profile.logo_path && <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50"><FileCheck className="w-3 h-3 mr-1" /> Téléversé</Badge>}
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="upload-logo-input" />
                <span className="inline-flex items-center px-3 py-2 border border-slate-300 rounded-md text-sm hover:bg-slate-50">
                  <Upload className="w-4 h-4 mr-1.5" /> {uploading ? "..." : "Téléverser"}
                </span>
              </label>
            </div>
          </div>
          <div><Label>Nom de l'entreprise</Label>
            <Input value={profile.company_name || ""} onChange={(e) => setProfile({ ...profile, company_name: e.target.value })} data-testid="emp-company" />
          </div>
          <div><Label>Secteur</Label>
            <Input value={profile.sector || ""} onChange={(e) => setProfile({ ...profile, sector: e.target.value })} data-testid="emp-sector" />
          </div>
          <div><Label>Description</Label>
            <Textarea rows={5} value={profile.description || ""} onChange={(e) => setProfile({ ...profile, description: e.target.value })} data-testid="emp-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Site web</Label>
              <Input value={profile.website || ""} onChange={(e) => setProfile({ ...profile, website: e.target.value })} />
            </div>
            <div><Label>Taille</Label>
              <Input value={profile.size || ""} onChange={(e) => setProfile({ ...profile, size: e.target.value })} placeholder="1-10, 50-200..." />
            </div>
          </div>
          <Button onClick={save} disabled={saving} className="bg-[#004EEB] hover:bg-[#003BCC] text-white" data-testid="save-profile">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </div>
      </div>
    );
  }

  // Candidate
  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="profile-candidate">
      <div className="mec-overline mb-2">Mon profil</div>
      <h1 className="font-display text-3xl font-bold text-slate-900 mb-6">Profil candidat</h1>

      <Tabs defaultValue="profile">
        <TabsList className="mb-5">
          <TabsTrigger value="profile" data-testid="tab-profile"><User2 className="w-4 h-4 mr-1.5" /> Profil & CV</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts"><Bell className="w-4 h-4 mr-1.5" /> Alertes emploi</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className="mec-card p-6 space-y-4">
        <div>
          <Label>CV (PDF, DOC, DOCX)</Label>
          <div className="mt-1 flex items-center gap-3">
            {profile.cv_filename && <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50"><FileCheck className="w-3 h-3 mr-1" /> {profile.cv_filename}</Badge>}
            <label className="cursor-pointer">
              <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleCVUpload} data-testid="upload-cv-input" />
              <span className="inline-flex items-center px-3 py-2 border border-slate-300 rounded-md text-sm hover:bg-slate-50">
                <Upload className="w-4 h-4 mr-1.5" /> {uploading ? "..." : "Téléverser CV"}
              </span>
            </label>
          </div>
        </div>
        <div><Label>Titre / Headline</Label>
          <Input value={profile.headline || ""} onChange={(e) => setProfile({ ...profile, headline: e.target.value })} placeholder="Ex: Développeur Full Stack senior" data-testid="cand-headline" />
        </div>
        <div><Label>Bio</Label>
          <Textarea rows={4} value={profile.bio || ""} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} data-testid="cand-bio" />
        </div>

        <div>
          <Label>Compétences</Label>
          <div className="flex gap-2 mt-1">
            <Input value={skillInput} onChange={(e) => setSkillInput(e.target.value)} placeholder="Ajouter une compétence" data-testid="skill-input"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (skillInput.trim()) { setProfile({ ...profile, skills: [...(profile.skills || []), skillInput.trim()] }); setSkillInput(""); } } }} />
            <Button type="button" variant="outline" onClick={() => { if (skillInput.trim()) { setProfile({ ...profile, skills: [...(profile.skills || []), skillInput.trim()] }); setSkillInput(""); } }}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(profile.skills || []).map((s, i) => (
              <Badge key={i} variant="outline" className="cursor-pointer hover:bg-red-50 hover:text-red-700 hover:border-red-200" onClick={() => setProfile({ ...profile, skills: profile.skills.filter((_, j) => j !== i) })}>
                {s} <Trash2 className="w-3 h-3 ml-1" />
              </Badge>
            ))}
          </div>
        </div>

        <div>
          <Label>Langues</Label>
          <div className="flex gap-2 mt-1">
            <Input value={langInput} onChange={(e) => setLangInput(e.target.value)} placeholder="Français, Anglais courant..."
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (langInput.trim()) { setProfile({ ...profile, languages: [...(profile.languages || []), langInput.trim()] }); setLangInput(""); } } }} />
            <Button type="button" variant="outline" onClick={() => { if (langInput.trim()) { setProfile({ ...profile, languages: [...(profile.languages || []), langInput.trim()] }); setLangInput(""); } }}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(profile.languages || []).map((l, i) => (
              <Badge key={i} variant="outline" className="cursor-pointer hover:bg-red-50" onClick={() => setProfile({ ...profile, languages: profile.languages.filter((_, j) => j !== i) })}>
                {l} ✕
              </Badge>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Poste recherché</Label>
            <Input value={profile.desired_role || ""} onChange={(e) => setProfile({ ...profile, desired_role: e.target.value })} />
          </div>
          <div><Label>Type de contrat</Label>
            <Select value={profile.desired_contract || ""} onValueChange={(v) => setProfile({ ...profile, desired_contract: v })}>
              <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="CDI">CDI</SelectItem>
                <SelectItem value="CDD">CDD</SelectItem>
                <SelectItem value="Freelance">Freelance</SelectItem>
                <SelectItem value="Stage">Stage</SelectItem>
                <SelectItem value="Alternance">Alternance</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Switch checked={!!profile.open_to_remote} onCheckedChange={(v) => setProfile({ ...profile, open_to_remote: v })} data-testid="open-remote" />
          <Label>Ouvert au télétravail</Label>
        </div>

        <Button onClick={save} disabled={saving} className="bg-[#004EEB] hover:bg-[#003BCC] text-white" data-testid="save-candidate-profile">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Enregistrement..." : "Enregistrer"}
        </Button>
      </div>
        </TabsContent>

        <TabsContent value="alerts">
          <AlertSettings />
        </TabsContent>
      </Tabs>
    </div>
  );
}
